/**
 * Inhalt der Kanalregel-Leiste in Arbeitsreihenfolge: Kanäle, Apartments, Zeitraum, Bezeichnung,
 * dann Klartext und Server-Prüfung. Beim Bearbeiten kommen Verlauf und der Weg zum MinStay dazu.
 */
import { FormField, Input } from '@/shared/components';
import { formatDateTime } from '@/shared/lib/format';
import { ChannelCloseoutMinStayLink } from './ChannelCloseoutMinStayLink';
import { ChannelCloseoutOperations } from './ChannelCloseoutOperations';
import { ChannelCloseoutPeriodPicker } from './ChannelCloseoutPeriodPicker';
import {
  ChannelCloseoutApartmentPicker,
  ChannelCloseoutChannelPicker,
} from './ChannelCloseoutPickers';
import { ChannelCloseoutSummary } from './ChannelCloseoutSummary';
import type { useChannelCloseoutEditor } from './useChannelCloseoutEditor';

export function ChannelCloseoutEditorFields({
  editor,
  today,
  closeoutId,
  onOpenApartment,
}: {
  editor: ReturnType<typeof useChannelCloseoutEditor>;
  today: string;
  closeoutId: string | null;
  onOpenApartment: (propertyId: string, startDate: string, endDate: string) => void;
}) {
  const { draft, detail, loading } = editor;
  const catalog = editor.options.data;
  const connected = catalog ? [...new Set(catalog.channels.map((channel) => channel.code))] : null;
  return (
    <>
      <div className="space-y-2">
        <ChannelCloseoutChannelPicker
          connected={connected}
          selected={draft.channels}
          disabled={loading}
          onToggle={editor.toggleChannel}
        />
        {catalog &&
          (catalog.channels.length === 0 ? (
            <p className="text-sealwax text-[13px]">
              Für dieses Objekt ist bei Channex kein aktiver, zugeordneter Booking.com- oder
              Airbnb-Kanal verbunden. Neu verbundene Kanäle erscheinen nach etwa zehn Minuten.
            </p>
          ) : (
            <p className="text-ink-faint text-[11px]">
              Kanalliste von Channex, Stand {formatDateTime(catalog.catalogPublishedAtUtc)}
            </p>
          ))}
      </div>
      <ChannelCloseoutApartmentPicker
        apartments={editor.apartments}
        channels={draft.channels}
        selected={draft.propertyIds}
        disabled={loading}
        onToggle={editor.toggleApartment}
      />
      <ChannelCloseoutPeriodPicker
        today={today}
        startDate={draft.startDate}
        endDate={draft.endDate}
        disabled={loading}
        onChange={editor.setPeriod}
      />
      <FormField
        label="Bezeichnung"
        required
        hint={editor.nameSuggested ? 'Vorschlag aus Kanal und Zeitraum, frei änderbar' : undefined}
      >
        <Input
          value={draft.name}
          maxLength={120}
          disabled={loading}
          onChange={(event) => editor.setName(event.target.value)}
        />
      </FormField>
      <ChannelCloseoutSummary
        draft={draft}
        apartments={editor.apartments}
        ready={editor.ready}
        check={editor.check}
        closeoutId={closeoutId}
      />
      {detail.data && <ChannelCloseoutOperations operations={detail.data.operations} />}
      {detail.data && (
        <ChannelCloseoutMinStayLink
          propertyIds={detail.data.propertyIds}
          apartments={editor.apartments}
          selected={editor.minStayApartmentId}
          onSelect={editor.setMinStayApartmentId}
          onOpen={() => onOpenApartment(editor.minStayApartmentId, draft.startDate, draft.endDate)}
        />
      )}
    </>
  );
}
